import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { X, Loader2 } from "lucide-react";
import { IconCheck, IconGift, IconCalendar, IconPhoto, IconVideo, IconPresentation, IconDatabase, IconCircleDashedCheck } from "@tabler/icons-react";
import { useSubscription } from "@/hooks/useSubscription";
import { PLUS_PRICE } from "@/config/pricing";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useOptimisticCredits } from "@/hooks/useOptimisticCredits";
interface PricingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isTrialMode?: boolean;
  onTrialComplete?: () => void;
  currentSubscription?: {
    subscription_tier?: string;
    subscribed?: boolean;
    subscription_end?: string;
    isTrialExpired?: boolean;
    isActive?: boolean;
    isOnTrial?: boolean;
  };
}
const PricingModal = ({
  open,
  onOpenChange,
  isTrialMode = false,
  onTrialComplete,
  currentSubscription
}: PricingModalProps) => {
  const [isActivatingTrial, setIsActivatingTrial] = useState(false);
  const [isManagingPlan, setIsManagingPlan] = useState(false);
  const [checkingOutPlan, setCheckingOutPlan] = useState<'monthly' | 'annual' | null>(null);
  const {
    createCheckout,
    subscription
  } = useSubscription();
  const {
    toast
  } = useToast();
  const queryClient = useQueryClient();
  const {
    user
  } = useAuth();
  const {
    refreshCredits
  } = useOptimisticCredits();
  const handleUpgrade = async (planType: "Plus", billingCycle: "monthly" | "annual") => {
    try {
      setCheckingOutPlan(billingCycle);
      await createCheckout(planType, billingCycle);
      // Refresh subscription status after successful checkout redirect
      setTimeout(() => {
        refreshCredits();
        queryClient.invalidateQueries({
          queryKey: ['subscription-status']
        });
      }, 1000);
    } catch (error) {
      console.error('Failed to create checkout:', error);
      toast({
        title: "Error",
        description: "Failed to start upgrade process. Please try again.",
        variant: "destructive"
      });
    } finally {
      setCheckingOutPlan(null);
    }
  };
  const handleManagePlan = async () => {
    setIsManagingPlan(true);
    try {
      const {
        data,
        error
      } = await supabase.functions.invoke('customer-portal');
      if (error) throw error;
      if (data?.url) {
        window.open(data.url, '_blank');
        // Refresh subscription status when user returns
        setTimeout(() => {
          refreshCredits();
          queryClient.invalidateQueries({
            queryKey: ['subscription-status']
          });
        }, 5000);
      }
    } catch (error) {
      console.error('Failed to open customer portal:', error);
      toast({
        title: "Error",
        description: "Failed to open plan management. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsManagingPlan(false);
    }
  };
  const isAnyOperationInProgress = isActivatingTrial || isManagingPlan || checkingOutPlan !== null;

  const handleStartTrial = async (planType: "Plus") => {
    if (isActivatingTrial) return;
    setIsActivatingTrial(true);
    try {
      const {
        data,
        error
      } = await supabase.functions.invoke('activate-trial', {
        body: {
          planType
        }
      });
      if (error) throw error;

      // Refresh credits optimistically and invalidate cache
      refreshCredits();
      toast({
        title: 'Trial activated successfully!',
        description: `Your ${planType} trial is now active. Enjoy exploring all features!`
      });

      // Small delay to ensure UI state refreshes before navigation
      setTimeout(() => {
        if (onTrialComplete) {
          onTrialComplete();
        } else {
          onOpenChange(false);
        }
      }, 500);
    } catch (error: any) {
      toast({
        title: 'Trial activation failed',
        description: error?.message || 'Please try again later',
        variant: 'destructive'
      });
    } finally {
      setIsActivatingTrial(false);
    }
  };
  return <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-none w-screen h-screen p-0 border-0 rounded-none m-0" style={{
      backgroundColor: '#F8F8F7'
    }} hideCloseButton>
        <div className="relative w-full h-full overflow-y-auto" style={{
        backgroundColor: '#F8F8F7'
      }}>
          {/* Close button */}
          <Button variant="ghost" size="icon" className="absolute top-6 right-6 z-10 w-10 h-10 rounded-full bg-muted/20 hover:bg-muted/40 text-muted-foreground hover:text-foreground border border-border/20 shadow-md hover:shadow-lg transition-all duration-200" onClick={() => onOpenChange(false)}>
            <X className="h-4 w-4" />
          </Button>

          <div className="p-8 pt-8 min-h-full flex flex-col justify-center">
            <DialogHeader className="text-center mb-8">
              <div className="flex justify-center mb-6">
                <img 
                  src="/lovable-uploads/5aaea69d-6bf3-4cb3-a3f4-da4910c5bbbc.png" 
                  alt="StudiBudi Logo" 
                  className="h-16 w-auto"
                />
              </div>
              <DialogTitle className="mb-1 text-center font-medium text-2xl">
                {isTrialMode ? "Choose your plan" : "Upgrade your plan for more credits"}
              </DialogTitle>
              
              {/* Current subscription context */}
              {currentSubscription?.subscribed && !isTrialMode && <div className="mt-4 p-4 bg-muted/30 rounded-lg border">
                  <p className="text-sm text-muted-foreground">
                    Currently on <span className="font-medium text-foreground">{currentSubscription.subscription_tier}</span> plan
                    {currentSubscription.subscription_end && <span> • Renews {new Date(currentSubscription.subscription_end).toLocaleDateString()}</span>}
                  </p>
                  {currentSubscription.isOnTrial && <p className="text-xs text-muted-foreground mt-1">
                      🎯 You're on a free trial • No charges until trial ends
                    </p>}
                </div>}
              
              {isTrialMode && <p className="text-muted-foreground text-lg mt-2 text-center">
                  Start using StudiBudi for FREE today!
                </p>}
            </DialogHeader>

            {/* Pricing cards */}
            <div className="flex justify-center max-w-6xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-4xl">
                
                {/* Monthly Plus Plan */}
                <div className="relative bg-card border border-border rounded-2xl p-8">
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-xl font-semibold">Plus Monthly</h3>
                    </div>
                    <div className="mb-2">
                      <span className="text-3xl font-bold">$29</span>
                      <span className="text-muted-foreground">/month</span>
                      <div className="text-sm text-muted-foreground h-5">
                        {/* Placeholder for consistent height with annual plan */}
                      </div>
                    </div>
                  </div>

                  <Button className="w-full mb-6 rounded-2xl" onClick={() => {
                  if (isTrialMode) {
                    handleStartTrial("Plus");
                  } else if (currentSubscription?.subscription_tier?.includes('Plus')) {
                    handleManagePlan();
                  } else {
                    handleUpgrade("Plus", "monthly");
                  }
                }} disabled={isAnyOperationInProgress} variant={currentSubscription?.subscription_tier?.includes('Plus') ? "outline" : "default"}>
                    {(isActivatingTrial || isManagingPlan || checkingOutPlan === 'monthly') && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {isActivatingTrial ? 'Activating Trial...' : isManagingPlan ? 'Opening Portal...' : checkingOutPlan === 'monthly' ? 'Creating Checkout...' : isTrialMode ? 'Start Plus Trial' : currentSubscription?.subscription_tier?.includes('Plus') ? 'Current Plan' : 'Start Monthly Plan'}
                  </Button>
                  
                  {/* Context message for Plus Monthly */}
                  {!isTrialMode && currentSubscription?.subscribed && <div className="mb-4 p-2 bg-muted/20 rounded-lg">
                      <p className="text-xs text-muted-foreground text-center">
                        {currentSubscription.subscription_tier?.includes('Plus') ? 'Your current plan' : 'Upgrade takes effect immediately'}
                      </p>
                    </div>}

                  <div className="space-y-4">
                    <div className="space-y-1.5">
                      {[{
                      text: "1,500 credits per month",
                      icon: "check",
                      highlight: true
                    }, {
                      text: "Voice conversation features",
                      icon: "check"
                    }, {
                      text: "Study session tracking",
                      icon: "check"
                    }, {
                      text: "Document analysis & upload",
                      icon: "check"
                    }, {
                      text: "PDF viewer integration",
                      icon: "check"
                    }, {
                      text: "Basic study analytics",
                      icon: "check"
                    }, {
                      text: "Priority AI responses",
                      icon: "check"
                    }, {
                      text: "Advanced document processing",
                      icon: "check"
                    }, {
                      text: "Priority customer support",
                      icon: "check"
                     }].map((feature, index) => <div key={index} className="flex items-center text-sm">
                          <IconCircleDashedCheck size={16} className="text-black mr-3 flex-shrink-0" />
                          <span className={feature.highlight ? "font-medium" : ""}>
                            {feature.text}
                          </span>
                        </div>)}
                    </div>
                  </div>
                </div>

                {/* Annual Plus Plan */}
                <div className="relative bg-card border-2 border-primary rounded-2xl p-8">
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-primary text-primary-foreground px-3 py-1">
                      Best Value
                    </Badge>
                  </div>
                  
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-xl font-semibold">Plus Annual</h3>
                      <Badge variant="outline" className="text-xs border-green-200 bg-green-50 text-green-700">
                        Save 20%
                      </Badge>
                    </div>
                    <div className="mb-2">
                      <span className="text-3xl font-bold">$290</span>
                      <span className="text-muted-foreground">/year</span>
                      <div className="text-sm text-muted-foreground">
                        <span className="line-through">$348</span> 
                        <span className="text-green-600 font-medium ml-2">Save $58</span>
                      </div>
                    </div>
                  </div>

                  <Button className="w-full mb-6 rounded-2xl" onClick={() => {
                  if (isTrialMode) {
                    handleStartTrial("Plus");
                  } else if (currentSubscription?.subscription_tier?.includes('Plus')) {
                    handleManagePlan();
                  } else {
                    handleUpgrade("Plus", "annual");
                  }
                }} disabled={isAnyOperationInProgress} variant={currentSubscription?.subscription_tier?.includes('Plus') ? "outline" : "default"}>
                    {(isActivatingTrial || isManagingPlan || checkingOutPlan === 'annual') && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {isActivatingTrial ? 'Activating Trial...' : isManagingPlan ? 'Opening Portal...' : checkingOutPlan === 'annual' ? 'Creating Checkout...' : isTrialMode ? 'Start Plus Trial' : currentSubscription?.subscription_tier?.includes('Plus') ? 'Switch to Annual' : 'Start Annual Plan'}
                  </Button>
                  
                  {/* Context message for Plus Annual */}
                  {!isTrialMode && currentSubscription?.subscribed && <div className="mb-4 p-2 bg-muted/20 rounded-lg">
                      <p className="text-xs text-muted-foreground text-center">
                        {currentSubscription.subscription_tier?.includes('Plus') ? 'Switch to annual billing and save' : 'Upgrade takes effect immediately'}
                      </p>
                    </div>}

                  <div className="space-y-4">
                    <div className="space-y-1.5">
                      {[{
                      text: "1,500 credits per month",
                      icon: "check",
                      highlight: true
                    }, {
                      text: "Voice conversation features",
                      icon: "check"
                    }, {
                      text: "Study session tracking",
                      icon: "check"
                    }, {
                      text: "Document analysis & upload",
                      icon: "check"
                    }, {
                      text: "PDF viewer integration",
                      icon: "check"
                    }, {
                      text: "Basic study analytics",
                      icon: "check"
                    }, {
                      text: "Priority AI responses",
                      icon: "check"
                    }, {
                      text: "Advanced document processing",
                      icon: "check"
                    }, {
                      text: "Priority customer support",
                      icon: "check"
                     }].map((feature, index) => <div key={index} className="flex items-center text-sm">
                          <IconCircleDashedCheck size={16} className="text-black mr-3 flex-shrink-0" />
                          <span className={feature.highlight ? "font-medium" : ""}>
                            {feature.text}
                          </span>
                        </div>)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>;
};
export default PricingModal;